import { Dispatch, SetStateAction } from "react";
import { useDashboardLeads, useDashboardLeadsFilters } from "@/common/states/store/slices/dashboardSlice";
import { format, parseISO } from "date-fns";
import { Modal } from "flowbite-react";
import { LeadsByOrigin } from "./LeadsByOrigin";
import { LeadsByCompany } from "../hooks/useDashLead";
import { normalizeCityName } from "@/common/helpers/company.helpers";

type openModalType = {
  screen: string,
  value: boolean
}

export function LeadsByMetrics({
  isModalOpen,
  setIsModalOpen,
  leadsByTime,
  leadsByOrigin,
  leadsByCompany,
  leadsByCompanyFilter,
}: {
  isModalOpen: { screen: string, value: boolean };
  setIsModalOpen: Dispatch<SetStateAction<openModalType>>;
  leadsByOrigin: { origin: string; companyName?: string; count: number }[];
  leadsByTime: Record<string, Record<string, number>>;
  leadsByCompany: LeadsByCompany[];
  leadsByCompanyFilter: LeadsByCompany[];
}) {
  const { dateRange } = useDashboardLeadsFilters();
  const { modalFilter, setModalStatus } = useDashboardLeads()
  const { status } = modalFilter

  const cityFilter = normalizeCityName(isModalOpen.screen);
  const filteredEntries = Object.entries(leadsByTime)
    .map(([hour, cities]) => [
      hour,
      cityFilter && cities[cityFilter] !== undefined
        ? { [cityFilter]: cities[cityFilter] }
        : cityFilter
          ? cities
          : {}
    ] as [string, Record<string, number>])
    .filter(([, cities]) => Object.values(cities).some(val => val > 0))
    .sort(([hA], [hB]) => parseInt(hA) - parseInt(hB));

  const total = filteredEntries.reduce((acc, [, cities]) => {
    return acc + Object.values(cities).reduce((sum, val) => sum + val, 0);
  }, 0);

  const peakHour = filteredEntries.reduce(
    (acc, [hour, cities]) => {
      const currentHourTotal = Object.values(cities).reduce((sum, val) => sum + val, 0);
      return currentHourTotal > acc.total ? { hour, total: currentHourTotal } : acc;
    },
    { hour: "", total: 0 }
  );

  const filteredLeadsByCompany =
    isModalOpen.screen === "period" ? leadsByCompanyFilter : leadsByCompany

  const handleCloseModal = () => {
    setModalStatus('');
    setIsModalOpen(prev => ({ ...prev, value: false }));
  }

  return (
    <Modal
      show={isModalOpen.value}
      size="7xl"
      className="relative"
      onClose={handleCloseModal}
      popup
    >
      <Modal.Header className="text-black p-4">
        <p className="text-2xl font-semibold">
          {isModalOpen.screen !== 'period'
            ? 'Total de Leads - Hoje'
            : `Total de Leads - ${format(parseISO(dateRange.start), "dd/MM/yyyy")} à ${format(parseISO(dateRange.end), "dd/MM/yyyy")}`}
        </p>
      </Modal.Header>

      <Modal.Body className="mt-2 space-y-4">
        {filteredEntries.length === 0 && isModalOpen.screen === undefined ? (
          <p className="text-gray-500 text-sm">Nenhum lead registrado no periodo.</p>
        ) : (
          <div className="flex flex-col md:flex-row md:space-x-8">
            <div className={`flex-1 ${isModalOpen.screen === 'period' ? 'hidden' : ''}`}>
              <h3 className="text-base font-semibold text-gray-800 mb-4">Leads por Horário</h3>
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  Total Geral: <strong>{total.toLocaleString("pt-BR")}</strong> &middot; Pico:{" "}
                  <strong>{peakHour.hour}</strong> (<strong>{peakHour.total.toLocaleString("pt-BR")}</strong>)
                </div>

                <div className="space-y-2">
                  {filteredEntries.map(([hour, cities]) => {
                    const hourTotal = Object.values(cities).reduce((sum, val) => sum + val, 0);
                    const pct = (hourTotal / peakHour.total) * 100;

                    return (
                      <div key={hour} className="flex items-center gap-3">
                        <span className="w-12 tabular-nums text-sm text-gray-700">{hour}</span>
                        <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden">
                          <div
                            className="h-3 bg-locavibe"
                            style={{ width: `${pct}%` }}
                            title={`${hour} — ${hourTotal.toLocaleString("pt-BR")}`}
                          />
                        </div>
                        <span className="w-12 text-right tabular-nums text-sm text-gray-700">
                          {hourTotal.toLocaleString("pt-BR")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={`border-l border-gray-200 ${isModalOpen.screen === 'period' ? 'hidden' : 'md:block'}`}></div>

            {isModalOpen.screen !== "period" && (
              <LeadsByOrigin leadsByOrigin={leadsByOrigin} cityFilter={cityFilter} />
            )}

            <div className={`flex-1 space-y-2 mt-4 md:mt-0 ${isModalOpen.screen !== 'period' ? 'hidden' : ''}`}>
              <h3 className="text-base font-semibold text-gray-800 mb-4">Leads por Cidade</h3>
              <div className="space-y-2">
                {Object.values(
                  filteredLeadsByCompany
                    ?.filter(item => {
                      if (!status) return true;
                      return item.status === status;
                    })
                    ?.reduce((acc, current) => {
                      const companyName = current.companyName;

                      if (!acc[companyName as string]) {
                        acc[companyName as string] = {
                          _id: current._id as string,
                          companyName: companyName as string,
                          count: 0,
                          status: current.status as string
                        };
                      }

                      acc[companyName as string].count += current.count || 0;
                      return acc;
                    }, {} as Record<string, { _id: string; companyName: string; count: number; status: string }>)
                )
                  ?.map((item) => (
                    <div key={item._id} className="flex justify-between items-center text-sm text-gray-600">
                      <span>{item.companyName}</span>
                      <div className="flex items-center gap-2">
                        <strong className="text-gray-800">{item.count?.toLocaleString("pt-BR")}</strong>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

